import { PageProps } from 'gatsby';
import React from 'react';
import useExperimentCustom from 'src/hooks/useExperimentCustom';
import { ExperimentSharedProvider } from 'src/hooks/useExperimentSharedContext';

type AbTestingServiceProps<TControl, TVariant> = {
  experimentName?: string;
  getterLabel?: string;
  props?: PageProps;
  control: { data?: TControl; template: React.FunctionComponent<any> };
  variant?: { data?: TVariant; template: React.FunctionComponent<any> };
};

const AbTestingService = <TControl, TVariant>(props: AbTestingServiceProps<TControl, TVariant>): JSX.Element => {
  const Control = props.control.template as React.FunctionComponent<any>;
  const controlProps = Object.assign({}, props.props, {
    data: props.control.data,
  });
  const isScriptless = process.env.GATSBY_SITE_ENV === 'scriptless';

  if (!props.experimentName || isScriptless) {
    return <Control {...controlProps} />;
  }

  const experimentResult = useExperimentCustom<boolean>([
    {
      experiment: props.experimentName,
      default: false,
      getter: props.getterLabel,
    },
  ]);

  // Page getter values should always be boolean
  const experimentSetup = experimentResult[props.getterLabel];

  const useVariant = experimentSetup.value;

  const Variant = props.variant?.template as React.FunctionComponent<any>;
  const variantProps = Object.assign({}, props.props, {
    data: props.variant?.data,
  });

  if (experimentSetup.isLoading) {
    return (
      <div style={{ opacity: 0 }}>
        <Control {...controlProps} />
      </div>
    );
  }

  const VariantToUse = useVariant ? Variant : Control;
  const propsToUse = useVariant ? variantProps : controlProps;

  return (
    <ExperimentSharedProvider
      experiment={props.experimentName}
      value={useVariant}
    >
      <VariantToUse {...propsToUse} />
    </ExperimentSharedProvider>
  );
};

export default AbTestingService;
